// =============================================================================
// iTravel 2.0 — Offline Database Manager (SQLite via Capacitor)
// =============================================================================
// Provides a local SQLite database for offline-first data caching and sync.
//
// Architecture:
//   - Uses @capacitor-community/sqlite for native SQLite on iOS/Android
//   - Falls back to in-memory Maps for web/dev (no native SQLite on web)
//   - Stores local copies of trips, days, points, checklist items
//   - Maintains a mutation queue for offline writes
//   - Integrates with the sync engine to replay mutations when online
//
// Tables:
//   - trips           — Cached trip data
//   - trip_days        — Cached trip day data
//   - travel_points    — Cached travel point data
//   - checklist_items  — Cached checklist item data
//   - sync_queue       — Pending mutations to be synced with server
//   - sync_meta        — Metadata about last sync timestamps
// =============================================================================

import { CapacitorSQLite, SQLiteConnection, SQLiteDBConnection } from '@capacitor-community/sqlite';
import { Capacitor } from '@capacitor/core';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * PENDING/SYNCING/SYNCED/FAILED are the retry path. DEAD is terminal: the
 * server rejected the mutation on its merits, so replaying it can only produce
 * the same rejection. Those entries leave the queue and go to the user.
 */
export type SyncStatus = 'PENDING' | 'SYNCING' | 'SYNCED' | 'FAILED' | 'DEAD';

export interface SyncQueueEntry {
  id: string;
  entityType: string;
  entityId: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  payload: string; // JSON-serialized payload
  status: SyncStatus;
  createdAt: string;
  retryCount: number;
  lastError: string | null;
  /** The server's own error code, when it sent one. Says *why* it refused. */
  lastErrorCode: string | null;
}

export interface SyncMeta {
  key: string;
  value: string;
  updatedAt: string;
}

export interface CachedTrip {
  id: string;
  data: string; // JSON-serialized TripDetailResponse
  updatedAt: string;
  syncedAt: string | null;
}

/** What a caller supplies when parking a photo for later. */
export interface PendingPhotoUploadInput {
  /** Also the server-side photo id, so a retry cannot create a duplicate. */
  photoId: string;
  tripId: string;
  /** Filesystem path of the scaled 1600px copy. */
  localUri: string;
  /** Filesystem path of the thumbnail, which also feeds the offline grid. */
  localThumbnailUri?: string | null;
  caption?: string | null;
  travelPointId?: string | null;
  tripDayId?: string | null;
  fileSize?: number | null;
  mimeType?: string;
  width?: number | null;
  height?: number | null;
  takenAt?: string | null;
  /** The calendar date the camera showed, as YYYY-MM-DD. */
  takenOnDate?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  exifData?: Record<string, unknown> | null;
}

/** A row as it comes back out — snake_case, straight from SQLite. */
export interface PendingPhotoUploadRow {
  id: string;
  trip_id: string;
  local_uri: string;
  local_thumbnail_uri: string | null;
  caption: string | null;
  travel_point_id: string | null;
  trip_day_id: string | null;
  file_size: number | null;
  mime_type: string;
  width: number | null;
  height: number | null;
  taken_at: string | null;
  taken_on_date: string | null;
  latitude: number | null;
  longitude: number | null;
  exif_data: string | null;
  status: 'PENDING' | 'SYNCING' | 'FAILED' | 'DEAD';
  retry_count: number;
  last_error: string | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// One database file per account rather than one shared file with a user_id
// column on every table. Isolation then holds by construction: there is no
// query that *could* read another account's rows, so no query has to remember
// to filter. Two people sharing a phone keep separate caches and separate
// outboxes, and each still has their unsent work waiting when they log back in.
const DB_NAME_PREFIX = 'itravel_offline';
const DB_VERSION = 1;

/** UUIDs are already safe; the filter is here so a future id shape cannot
    smuggle path or SQL characters into a database filename. */
export function dbNameForUser(userId: string): string {
  return `${DB_NAME_PREFIX}_${userId.replace(/[^a-zA-Z0-9-]/g, '')}`;
}

// ---------------------------------------------------------------------------
// Schema Migrations
// ---------------------------------------------------------------------------

const MIGRATIONS: string[] = [
  // Version 1 — Initial schema
  `
  -- Cached trips (full detail responses)
  CREATE TABLE IF NOT EXISTS trips (
    id TEXT PRIMARY KEY NOT NULL,
    data TEXT NOT NULL,
    updated_at TEXT NOT NULL DEFAULT (datetime('now')),
    synced_at TEXT
  );

  -- Cached trip days
  CREATE TABLE IF NOT EXISTS trip_days (
    id TEXT PRIMARY KEY NOT NULL,
    trip_id TEXT NOT NULL,
    data TEXT NOT NULL,
    updated_at TEXT NOT NULL DEFAULT (datetime('now')),
    FOREIGN KEY (trip_id) REFERENCES trips(id) ON DELETE CASCADE
  );

  -- Cached travel points
  CREATE TABLE IF NOT EXISTS travel_points (
    id TEXT PRIMARY KEY NOT NULL,
    trip_day_id TEXT NOT NULL,
    trip_id TEXT NOT NULL,
    data TEXT NOT NULL,
    updated_at TEXT NOT NULL DEFAULT (datetime('now')),
    FOREIGN KEY (trip_day_id) REFERENCES trip_days(id) ON DELETE CASCADE
  );

  -- Cached checklist items
  CREATE TABLE IF NOT EXISTS checklist_items (
    id TEXT PRIMARY KEY NOT NULL,
    trip_id TEXT NOT NULL,
    data TEXT NOT NULL,
    updated_at TEXT NOT NULL DEFAULT (datetime('now')),
    FOREIGN KEY (trip_id) REFERENCES trips(id) ON DELETE CASCADE
  );

  -- Sync queue — mutations waiting to be sent to server
  CREATE TABLE IF NOT EXISTS sync_queue (
    id TEXT PRIMARY KEY NOT NULL,
    entity_type TEXT NOT NULL,
    entity_id TEXT NOT NULL,
    action TEXT NOT NULL CHECK (action IN ('CREATE', 'UPDATE', 'DELETE')),
    payload TEXT NOT NULL,
    status TEXT NOT NULL DEFAULT 'PENDING' CHECK (status IN ('PENDING', 'SYNCING', 'SYNCED', 'FAILED')),
    created_at TEXT NOT NULL DEFAULT (datetime('now')),
    retry_count INTEGER NOT NULL DEFAULT 0,
    last_error TEXT
  );

  -- Sync metadata — last sync timestamps per entity type
  CREATE TABLE IF NOT EXISTS sync_meta (
    key TEXT PRIMARY KEY NOT NULL,
    value TEXT NOT NULL,
    updated_at TEXT NOT NULL DEFAULT (datetime('now'))
  );

  -- Indexes for efficient querying
  CREATE INDEX IF NOT EXISTS idx_trip_days_trip_id ON trip_days(trip_id);
  CREATE INDEX IF NOT EXISTS idx_travel_points_trip_day_id ON travel_points(trip_day_id);
  CREATE INDEX IF NOT EXISTS idx_travel_points_trip_id ON travel_points(trip_id);
  CREATE INDEX IF NOT EXISTS idx_checklist_items_trip_id ON checklist_items(trip_id);
  CREATE INDEX IF NOT EXISTS idx_sync_queue_status ON sync_queue(status);
  CREATE INDEX IF NOT EXISTS idx_sync_queue_entity ON sync_queue(entity_type, entity_id);
  CREATE INDEX IF NOT EXISTS idx_sync_queue_created ON sync_queue(created_at);
  `,

  // Version 2 — a status for "the server has rejected this and always will"
  //
  // Everything used to be retryable. A 422 the server will never accept was
  // treated exactly like a dropped connection: retried with backoff, and
  // because the pending query also selected FAILED rows, retried again on
  // every sync cycle after that. It churned forever and kept the engine
  // permanently in an error state.
  //
  // DEAD entries stop being retried and are surfaced to the user instead.
  // SQLite cannot alter a CHECK constraint, so the table is rebuilt.
  `
  CREATE TABLE sync_queue_v2 (
    id TEXT PRIMARY KEY NOT NULL,
    entity_type TEXT NOT NULL,
    entity_id TEXT NOT NULL,
    action TEXT NOT NULL CHECK (action IN ('CREATE', 'UPDATE', 'DELETE')),
    payload TEXT NOT NULL,
    status TEXT NOT NULL DEFAULT 'PENDING'
      CHECK (status IN ('PENDING', 'SYNCING', 'SYNCED', 'FAILED', 'DEAD')),
    created_at TEXT NOT NULL DEFAULT (datetime('now')),
    retry_count INTEGER NOT NULL DEFAULT 0,
    last_error TEXT
  );

  INSERT INTO sync_queue_v2 (id, entity_type, entity_id, action, payload, status, created_at, retry_count, last_error)
    SELECT id, entity_type, entity_id, action, payload, status, created_at, retry_count, last_error FROM sync_queue;

  DROP TABLE sync_queue;
  ALTER TABLE sync_queue_v2 RENAME TO sync_queue;

  CREATE INDEX IF NOT EXISTS idx_sync_queue_status ON sync_queue(status);
  CREATE INDEX IF NOT EXISTS idx_sync_queue_entity ON sync_queue(entity_type, entity_id);
  CREATE INDEX IF NOT EXISTS idx_sync_queue_created ON sync_queue(created_at);
  `,

  // Version 3 — money, so the balance is readable without signal
  //
  // Kept as one row per trip holding the whole picture — list, balance,
  // settlements, stats — rather than modelling expenses relationally. Nothing
  // offline queries across them, and the balance in particular is a server
  // computation we would otherwise have to reimplement to derive locally.
  `
  CREATE TABLE IF NOT EXISTS trip_expenses (
    trip_id TEXT PRIMARY KEY NOT NULL,
    data TEXT NOT NULL,
    updated_at TEXT NOT NULL DEFAULT (datetime('now')),
    synced_at TEXT
  );
  `,

  // Version 4 — photos: an outbox that survives a restart, and enough cached
  // to browse the gallery on a plane.
  //
  // Photos do not go through sync_queue. Every other mutation is a small JSON
  // body that can sit in a column; a photo is a quarter-megabyte of JPEG that
  // belongs on disk, and it needs a second attempt at a different step —
  // re-signing an expired URL rather than re-sending a payload.
  `
  CREATE TABLE IF NOT EXISTS pending_photo_uploads (
    -- Also the server-side photo id. Chosen before the first attempt so a
    -- retry re-signs the same object keys instead of creating a second photo.
    id TEXT PRIMARY KEY NOT NULL,
    trip_id TEXT NOT NULL,
    -- Filesystem paths of the already-scaled copies. The bytes never live in
    -- this database; only where to find them does.
    local_uri TEXT NOT NULL,
    local_thumbnail_uri TEXT,
    caption TEXT,
    travel_point_id TEXT,
    trip_day_id TEXT,
    file_size INTEGER,
    mime_type TEXT NOT NULL DEFAULT 'image/jpeg',
    width INTEGER,
    height INTEGER,
    taken_at TEXT,
    latitude REAL,
    longitude REAL,
    exif_data TEXT,
    status TEXT NOT NULL DEFAULT 'PENDING'
      CHECK (status IN ('PENDING', 'SYNCING', 'FAILED', 'DEAD')),
    retry_count INTEGER NOT NULL DEFAULT 0,
    last_error TEXT,
    created_at TEXT NOT NULL DEFAULT (datetime('now'))
  );

  CREATE INDEX IF NOT EXISTS idx_pending_photos_status ON pending_photo_uploads(status);
  CREATE INDEX IF NOT EXISTS idx_pending_photos_trip ON pending_photo_uploads(trip_id);

  -- One row per trip holding the photo list as the server returned it, same
  -- shape as trip_expenses. Nothing offline queries across photos, so there is
  -- nothing to gain from modelling them relationally.
  CREATE TABLE IF NOT EXISTS trip_photos (
    trip_id TEXT PRIMARY KEY NOT NULL,
    data TEXT NOT NULL,
    updated_at TEXT NOT NULL DEFAULT (datetime('now')),
    synced_at TEXT
  );

  -- Where a photo's thumbnail is on this device, whoever put it there: the
  -- upload that produced it, or a background download afterwards. This is what
  -- lets the grid render with no connection.
  CREATE TABLE IF NOT EXISTS photo_files (
    photo_id TEXT PRIMARY KEY NOT NULL,
    trip_id TEXT NOT NULL,
    thumbnail_path TEXT NOT NULL,
    cached_at TEXT NOT NULL DEFAULT (datetime('now'))
  );

  CREATE INDEX IF NOT EXISTS idx_photo_files_trip ON photo_files(trip_id);
  `,

  // Version 5 — the calendar date a photo was taken on
  //
  // Added here rather than in migration 4 because that one has already shipped:
  // a device that ran it will never run it again, so editing it would give
  // fresh installs the column and leave every existing one without it — and the
  // insert would fail on exactly the devices that have photos waiting.
  //
  // Separate from taken_at because EXIF records wall time with no zone. A photo
  // taken at 01:00 in Tokyo is the previous day in UTC, so deriving the date
  // from the instant files it under the wrong day of the trip.
  `
  ALTER TABLE pending_photo_uploads ADD COLUMN taken_on_date TEXT;
  `,

  // Version 6 — why the server refused, not just that it did
  //
  // A 409 on a CREATE normally means "an earlier attempt landed and only the
  // response was lost", and the queue counts that as success. A settlement the
  // server suspects is a duplicate answers 409 as well, and means the opposite:
  // nothing was written, and a person has to decide.
  //
  // Told apart by status alone, the second was being swallowed as the first, so
  // a repayment recorded with no signal simply vanished. The server says which
  // it is; this is where that answer is kept.
  `
  ALTER TABLE sync_queue ADD COLUMN last_error_code TEXT;
  `,
];

/**
 * Splits a migration into executable statements.
 *
 * Comments are stripped per line rather than per statement. Dropping any chunk
 * that *began* with `--` looked equivalent and was not: splitting on `;` leaves
 * each comment attached to the statement below it, so every commented
 * `CREATE TABLE` was silently discarded. What survived were the few statements
 * with no comment above them — indexes, which then failed against tables that
 * had never been created. The throw was caught, the app fell back to an
 * in-memory database, and offline storage had never once worked.
 */
export function splitStatements(migration: string): string[] {
  return migration
    .split('\n')
    .map((line) => line.replace(/--.*$/, ''))
    .join('\n')
    .split(';')
    .map((statement) => statement.trim())
    .filter((statement) => statement.length > 0);
}

// ---------------------------------------------------------------------------
// In-Memory Fallback (for Web / Development)
// ---------------------------------------------------------------------------
// On web, Capacitor SQLite doesn't work natively, so we provide an in-memory
// fallback using Maps. This ensures the app still functions for development
// and PWA scenarios, though data won't persist across page reloads.
// ---------------------------------------------------------------------------

class InMemoryDB {
  private tables = new Map<string, Map<string, Record<string, unknown>>>();

  private getTable(name: string): Map<string, Record<string, unknown>> {
    if (!this.tables.has(name)) {
      this.tables.set(name, new Map());
    }
    return this.tables.get(name)!;
  }

  async run(table: string, id: string, data: Record<string, unknown>): Promise<void> {
    this.getTable(table).set(id, { ...data, id });
  }

  async get(table: string, id: string): Promise<Record<string, unknown> | null> {
    return this.getTable(table).get(id) ?? null;
  }

  async getAll(table: string): Promise<Record<string, unknown>[]> {
    return Array.from(this.getTable(table).values());
  }

  async query(
    table: string,
    predicate: (row: Record<string, unknown>) => boolean,
  ): Promise<Record<string, unknown>[]> {
    return Array.from(this.getTable(table).values()).filter(predicate);
  }

  async delete(table: string, id: string): Promise<void> {
    this.getTable(table).delete(id);
  }

  async deleteWhere(
    table: string,
    predicate: (row: Record<string, unknown>) => boolean,
  ): Promise<number> {
    const t = this.getTable(table);
    let count = 0;
    for (const [key, value] of t.entries()) {
      if (predicate(value)) {
        t.delete(key);
        count++;
      }
    }
    return count;
  }

  async clear(table: string): Promise<void> {
    this.getTable(table).clear();
  }

  async clearAll(): Promise<void> {
    this.tables.clear();
  }
}

// ---------------------------------------------------------------------------
// OfflineDatabase Class
// ---------------------------------------------------------------------------

export class OfflineDatabase {
  private static instance: OfflineDatabase | null = null;

  private sqliteConnection: SQLiteConnection | null = null;
  private db: SQLiteDBConnection | null = null;
  private memDb: InMemoryDB | null = null;
  private isNative: boolean;
  private initialized = false;
  private initPromise: Promise<void> | null = null;
  /** Whose database is currently open. Null means no session — every read and
      write fails closed rather than silently touching the wrong account. */
  private currentUserId: string | null = null;

  /** One in-flight expense write per trip — see cacheTripExpenses. */
  private expenseWrites = new Map<string, Promise<void>>();

  /** Resolves when a session opens. See waitForSession. */
  private sessionGate: Promise<void>;
  private openSessionGate: () => void = () => {};

  private constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.sessionGate = new Promise((resolve) => {
      this.openSessionGate = resolve;
    });
  }

  /**
   * Waits for a session to be open, up to `timeoutMs`. Returns whether one is.
   *
   * Screens start fetching the moment they mount, while opening the database
   * waits on auth hydration — so on a cold start they race. Online the request
   * takes long enough that the database always wins. Offline the request fails
   * in milliseconds and the fallback to disk arrived before there was a
   * database to read, got "no session open", and reported that the user had no
   * data at all. Waiting a moment is the difference between reading the cache
   * and declaring it empty.
   */
  async waitForSession(timeoutMs = 5_000): Promise<boolean> {
    if (this.currentUserId) return true;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, timeoutMs);
    });

    await Promise.race([this.sessionGate, timeout]);
    if (timer) clearTimeout(timer);

    return this.currentUserId !== null;
  }

  /**
   * Get the singleton instance of OfflineDatabase.
   */
  static getInstance(): OfflineDatabase {
    if (!OfflineDatabase.instance) {
      OfflineDatabase.instance = new OfflineDatabase();
    }
    return OfflineDatabase.instance;
  }

  /** Whose database is open, or null when nobody is signed in. */
  get sessionUserId(): string | null {
    return this.currentUserId;
  }

  /**
   * Open the database belonging to `userId`, closing another account's first.
   * Switching accounts leaves the previous one's cache and outbox on disk —
   * they are waiting when that person signs back in.
   */
  async openForUser(userId: string): Promise<void> {
    if (this.currentUserId === userId && this.initialized) return;

    if (this.currentUserId && this.currentUserId !== userId) {
      await this.close();
    }

    this.currentUserId = userId;
    await this.initialize();
    // Releases anything that started reading before the session existed.
    this.openSessionGate();
  }

  /**
   * Initialize the database. Safe to call multiple times — will only
   * initialize once and return the same promise on concurrent calls.
   *
   * Requires an open session: without one there is no way to know which
   * account's file to touch, and guessing is how data leaks between accounts.
   */
  async initialize(): Promise<void> {
    if (!this.currentUserId) {
      throw new Error('[OfflineDB] No session open — call openForUser(userId) first');
    }

    if (this.initialized) return;

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._doInitialize();
    await this.initPromise;
    this.initialized = true;
  }

  /**
   * Applies only the migrations this database has not seen.
   *
   * Tracked in a table of our own rather than `PRAGMA user_version`, which
   * looked like the obvious place until it turned out the plugin uses that same
   * pragma for its own upgrade mechanism — `createConnection` declares a version
   * and compares against it. Two things writing one counter is a bug waiting
   * for the next schema change.
   */
  private async runMigrations(): Promise<void> {
    if (!this.db) return;

    await this.db.execute('CREATE TABLE IF NOT EXISTS schema_version (version INTEGER NOT NULL);');

    let applied = 0;
    const stored = await this.db.query('SELECT version FROM schema_version LIMIT 1;');
    if (stored.values && stored.values.length > 0) {
      applied = Number(stored.values[0]!.version ?? 0);
    } else {
      // A database from before this table existed may still have been stamped
      // through the pragma. Believe it, once, so its migrations are not re-run.
      try {
        const legacy = await this.db.query('PRAGMA user_version;');
        applied = Number(legacy.values?.[0]?.user_version ?? 0);
      } catch {
        applied = 0;
      }
      await this.db.run('INSERT INTO schema_version (version) VALUES (?);', [applied]);
    }

    for (let version = applied; version < MIGRATIONS.length; version++) {
      for (const statement of splitStatements(MIGRATIONS[version]!)) {
        await this.db.execute(statement + ';');
      }

      await this.db.run('UPDATE schema_version SET version = ?;', [version + 1]);
      console.info(`[OfflineDB] Applied migration ${version + 1}`);
    }
  }

  /**
   * Writes a row and reads it back.
   *
   * Opening a connection succeeding is not the same as storage working, and the
   * difference is invisible until someone restarts the app a day later and
   * finds their trips gone. This turns that into a line in the log at startup.
   */
  private async verifyStorage(): Promise<boolean> {
    if (!this.db) return false;
    try {
      const probe = `probe-${Date.now()}`;
      await this.db.run('INSERT OR REPLACE INTO sync_meta (key, value) VALUES (?, ?);', [
        'storageProbe',
        probe,
      ]);
      const read = await this.db.query('SELECT value FROM sync_meta WHERE key = ?;', [
        'storageProbe',
      ]);
      return read.values?.[0]?.value === probe;
    } catch (error) {
      console.error('[OfflineDB] Storage probe failed:', error);
      return false;
    }
  }

  private async _doInitialize(): Promise<void> {
    const dbName = dbNameForUser(this.currentUserId!);

    if (!this.isNative) {
      this.memDb = new InMemoryDB();
      console.info('[OfflineDB] In-memory database (web) — nothing survives a reload');
      return;
    }

    try {
      this.sqliteConnection = new SQLiteConnection(CapacitorSQLite);

      // Guarded on its own. This call throws when there is nothing to check —
      // which is every cold start — and it used to be the first statement
      // inside one big try, so the throw took the entire initialisation with
      // it and the app fell back to memory on every launch. Storage looked
      // like it worked right up until the app was restarted.
      let hasExistingConnection = false;
      try {
        const consistency = await this.sqliteConnection.checkConnectionsConsistency();
        const existing = await this.sqliteConnection.isConnection(dbName, false);
        hasExistingConnection = Boolean(consistency.result && existing.result);
      } catch {
        hasExistingConnection = false;
      }

      this.db = hasExistingConnection
        ? await this.sqliteConnection.retrieveConnection(dbName, false)
        : await this.sqliteConnection.createConnection(
            dbName,
            false,
            'no-encryption',
            DB_VERSION,
            false,
          );

      await this.db.open();
      await this.runMigrations();

      const persists = await this.verifyStorage();
      if (!persists) {
        throw new Error('opened, but a written row could not be read back');
      }

      console.info(`[OfflineDB] SQLite ready (${dbName}) — data persists across restarts`);
    } catch (error) {
      // Loud, and at error level. Everything still works in this state — for
      // exactly as long as the app stays open — so a quiet warning is how you
      // end up shipping an offline mode that has never once survived a restart.
      console.error(
        '[OfflineDB] NATIVE SQLITE UNAVAILABLE — falling back to memory. ' +
          'Nothing will survive an app restart.',
        error,
      );
      this.isNative = false;
      this.db = null;
      this.memDb = new InMemoryDB();
    }
  }

  /** Whether writes are actually reaching disk, for the UI to be honest about. */
  get isPersistent(): boolean {
    return this.isNative && this.db !== null;
  }

  // =========================================================================
  // EXPENSE CACHE
  // =========================================================================

  /**
   * Stores a trip's money in one row: the first page of the expense list, the
   * balance, the settlements and the stats.
   *
   * Merged rather than replaced, because the four arrive from four separate
   * requests. Writing each one whole would mean the balance wiping the list a
   * moment after it was stored — the same way the trip list used to empty
   * cached itineraries.
   */
  async cacheTripExpenses(tripId: string, partial: Record<string, unknown>): Promise<void> {
    // Serialised per trip. Merging is read-modify-write, and the four slots are
    // written by four requests that finish at roughly the same moment — so
    // without this they interleave and the last writer silently drops whatever
    // the others had just merged in. It showed up as a cache holding the
    // balance and the stats but not the list.
    const previous = this.expenseWrites.get(tripId) ?? Promise.resolve();
    const next = previous
      .catch(() => undefined)
      .then(() => this.writeTripExpenses(tripId, partial));

    this.expenseWrites.set(tripId, next);
    try {
      await next;
    } finally {
      if (this.expenseWrites.get(tripId) === next) {
        this.expenseWrites.delete(tripId);
      }
    }
  }

  private async writeTripExpenses(tripId: string, partial: Record<string, unknown>): Promise<void> {
    await this.initialize();

    const existing = ((await this.getCachedTripExpenses(tripId)) ?? {}) as Record<string, unknown>;
    const serialized = JSON.stringify({ ...existing, ...partial });
    const now = new Date().toISOString();

    if (this.isNative && this.db) {
      await this.db.run(
        `INSERT OR REPLACE INTO trip_expenses (trip_id, data, updated_at, synced_at)
         VALUES (?, ?, ?, ?)`,
        [tripId, serialized, now, now],
      );
    } else if (this.memDb) {
      await this.memDb.run('trip_expenses', tripId, {
        id: tripId,
        trip_id: tripId,
        data: serialized,
        updated_at: now,
        synced_at: now,
      });
    }
  }

  async getCachedTripExpenses(tripId: string): Promise<Record<string, unknown> | null> {
    await this.initialize();

    let raw: string | null = null;

    if (this.isNative && this.db) {
      const result = await this.db.query('SELECT data FROM trip_expenses WHERE trip_id = ?', [
        tripId,
      ]);
      raw = (result.values?.[0]?.data as string) ?? null;
    } else if (this.memDb) {
      const row = await this.memDb.get('trip_expenses', tripId);
      raw = (row?.data as string) ?? null;
    }

    if (!raw) return null;
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  // =========================================================================
  // PHOTO OPERATIONS
  // =========================================================================

  /**
   * Records a photo waiting to go out. The scaled files are already on disk by
   * the time this is called; the row only says where they are and what still
   * has to be told to the server.
   */
  async queuePhotoUpload(entry: PendingPhotoUploadInput): Promise<void> {
    await this.initialize();

    const now = new Date().toISOString();
    const row = {
      id: entry.photoId,
      trip_id: entry.tripId,
      local_uri: entry.localUri,
      local_thumbnail_uri: entry.localThumbnailUri ?? null,
      caption: entry.caption ?? null,
      travel_point_id: entry.travelPointId ?? null,
      trip_day_id: entry.tripDayId ?? null,
      file_size: entry.fileSize ?? null,
      mime_type: entry.mimeType ?? 'image/jpeg',
      width: entry.width ?? null,
      height: entry.height ?? null,
      taken_at: entry.takenAt ?? null,
      taken_on_date: entry.takenOnDate ?? null,
      latitude: entry.latitude ?? null,
      longitude: entry.longitude ?? null,
      exif_data: entry.exifData ? JSON.stringify(entry.exifData) : null,
      status: 'PENDING',
      retry_count: 0,
      last_error: null,
      created_at: now,
    };

    if (this.isNative && this.db) {
      await this.db.run(
        `INSERT OR REPLACE INTO pending_photo_uploads
           (id, trip_id, local_uri, local_thumbnail_uri, caption, travel_point_id, trip_day_id,
            file_size, mime_type, width, height, taken_at, taken_on_date, latitude, longitude,
            exif_data, status, retry_count, last_error, created_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          row.id,
          row.trip_id,
          row.local_uri,
          row.local_thumbnail_uri,
          row.caption,
          row.travel_point_id,
          row.trip_day_id,
          row.file_size,
          row.mime_type,
          row.width,
          row.height,
          row.taken_at,
          row.taken_on_date,
          row.latitude,
          row.longitude,
          row.exif_data,
          row.status,
          row.retry_count,
          row.last_error,
          row.created_at,
        ],
      );
    } else if (this.memDb) {
      await this.memDb.run('pending_photo_uploads', row.id, row);
    }
  }

  /** Everything still worth another attempt, oldest first. */
  async getPendingPhotoUploads(): Promise<PendingPhotoUploadRow[]> {
    await this.initialize();

    if (this.isNative && this.db) {
      const result = await this.db.query(
        `SELECT * FROM pending_photo_uploads
         WHERE status IN ('PENDING', 'FAILED')
         ORDER BY created_at ASC`,
      );
      return (result.values ?? []) as PendingPhotoUploadRow[];
    }

    if (this.memDb) {
      const rows = await this.memDb.query(
        'pending_photo_uploads',
        (row) => row.status === 'PENDING' || row.status === 'FAILED',
      );
      return (rows as unknown as PendingPhotoUploadRow[]).sort((a, b) =>
        String(a.created_at).localeCompare(String(b.created_at)),
      );
    }

    return [];
  }

  /** Photos the server has rejected in a way retrying will not fix. */
  async getDeadPhotoUploads(): Promise<PendingPhotoUploadRow[]> {
    await this.initialize();

    if (this.isNative && this.db) {
      const result = await this.db.query(
        `SELECT * FROM pending_photo_uploads WHERE status = 'DEAD' ORDER BY created_at ASC`,
      );
      return (result.values ?? []) as PendingPhotoUploadRow[];
    }

    if (this.memDb) {
      return (await this.memDb.query(
        'pending_photo_uploads',
        (row) => row.status === 'DEAD',
      )) as unknown as PendingPhotoUploadRow[];
    }

    return [];
  }

  async setPhotoUploadStatus(
    photoId: string,
    status: 'PENDING' | 'SYNCING' | 'FAILED' | 'DEAD',
    lastError?: string,
  ): Promise<void> {
    await this.initialize();

    if (this.isNative && this.db) {
      await this.db.run(
        `UPDATE pending_photo_uploads
         SET status = ?,
             last_error = ?,
             retry_count = retry_count + CASE WHEN ? IN ('FAILED', 'DEAD') THEN 1 ELSE 0 END
         WHERE id = ?`,
        [status, lastError ?? null, status, photoId],
      );
    } else if (this.memDb) {
      const row = await this.memDb.get('pending_photo_uploads', photoId);
      if (row) {
        await this.memDb.run('pending_photo_uploads', photoId, {
          ...row,
          status,
          last_error: lastError ?? null,
          retry_count:
            Number(row.retry_count ?? 0) + (status === 'FAILED' || status === 'DEAD' ? 1 : 0),
        });
      }
    }
  }

  async removePhotoUpload(photoId: string): Promise<void> {
    await this.initialize();

    if (this.isNative && this.db) {
      await this.db.run('DELETE FROM pending_photo_uploads WHERE id = ?', [photoId]);
    } else if (this.memDb) {
      await this.memDb.delete('pending_photo_uploads', photoId);
    }
  }

  async countPendingPhotoUploads(): Promise<number> {
    return (await this.getPendingPhotoUploads()).length;
  }

  // ---------------------------------------------------------------------------
  // Cached photo lists and files
  // ---------------------------------------------------------------------------

  async cacheTripPhotos(tripId: string, data: unknown): Promise<void> {
    await this.initialize();

    const serialized = JSON.stringify(data);
    const now = new Date().toISOString();

    if (this.isNative && this.db) {
      await this.db.run(
        `INSERT OR REPLACE INTO trip_photos (trip_id, data, updated_at, synced_at)
         VALUES (?, ?, ?, ?)`,
        [tripId, serialized, now, now],
      );
    } else if (this.memDb) {
      await this.memDb.run('trip_photos', tripId, {
        id: tripId,
        trip_id: tripId,
        data: serialized,
        updated_at: now,
        synced_at: now,
      });
    }
  }

  async getCachedTripPhotos<T>(tripId: string): Promise<T | null> {
    await this.initialize();

    let raw: string | null = null;

    if (this.isNative && this.db) {
      const result = await this.db.query('SELECT data FROM trip_photos WHERE trip_id = ?', [
        tripId,
      ]);
      raw = (result.values?.[0]?.data as string) ?? null;
    } else if (this.memDb) {
      const row = await this.memDb.get('trip_photos', tripId);
      raw = (row?.data as string) ?? null;
    }

    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  /** Notes that this photo's thumbnail is readable on this device. */
  async recordPhotoFile(photoId: string, tripId: string, thumbnailPath: string): Promise<void> {
    await this.initialize();

    const now = new Date().toISOString();

    if (this.isNative && this.db) {
      await this.db.run(
        `INSERT OR REPLACE INTO photo_files (photo_id, trip_id, thumbnail_path, cached_at)
         VALUES (?, ?, ?, ?)`,
        [photoId, tripId, thumbnailPath, now],
      );
    } else if (this.memDb) {
      await this.memDb.run('photo_files', photoId, {
        id: photoId,
        photo_id: photoId,
        trip_id: tripId,
        thumbnail_path: thumbnailPath,
        cached_at: now,
      });
    }
  }

  /** photoId → local thumbnail path, for every photo of one trip. */
  async getPhotoFilePaths(tripId: string): Promise<Record<string, string>> {
    await this.initialize();

    let rows: Array<Record<string, unknown>> = [];

    if (this.isNative && this.db) {
      const result = await this.db.query(
        'SELECT photo_id, thumbnail_path FROM photo_files WHERE trip_id = ?',
        [tripId],
      );
      rows = result.values ?? [];
    } else if (this.memDb) {
      rows = await this.memDb.query('photo_files', (row) => row.trip_id === tripId);
    }

    const paths: Record<string, string> = {};
    for (const row of rows) {
      const id = row.photo_id as string;
      const path = row.thumbnail_path as string;
      if (id && path) paths[id] = path;
    }
    return paths;
  }

  async forgetPhotoFile(photoId: string): Promise<void> {
    await this.initialize();

    if (this.isNative && this.db) {
      await this.db.run('DELETE FROM photo_files WHERE photo_id = ?', [photoId]);
    } else if (this.memDb) {
      await this.memDb.delete('photo_files', photoId);
    }
  }

  // =========================================================================
  // TRIP CACHE OPERATIONS
  // =========================================================================

  /**
   * Cache a trip's full detail response locally.
   */
  async cacheTrip(tripId: string, data: unknown): Promise<void> {
    await this.initialize();

    const serialized = JSON.stringify(data);
    const now = new Date().toISOString();

    if (this.isNative && this.db) {
      await this.db.run(
        `INSERT OR REPLACE INTO trips (id, data, updated_at, synced_at)
         VALUES (?, ?, ?, ?)`,
        [tripId, serialized, now, now],
      );
    } else if (this.memDb) {
      await this.memDb.run('trips', tripId, {
        id: tripId,
        data: serialized,
        updated_at: now,
        synced_at: now,
      });
    }
  }

  /**
   * Get a cached trip by ID.
   */
  async getCachedTrip(tripId: string): Promise<unknown | null> {
    await this.initialize();

    if (this.isNative && this.db) {
      const result = await this.db.query('SELECT data FROM trips WHERE id = ?', [tripId]);
      if (result.values && result.values.length > 0) {
        try {
          return JSON.parse(result.values[0].data as string);
        } catch {
          return null;
        }
      }
      return null;
    } else if (this.memDb) {
      const row = await this.memDb.get('trips', tripId);
      if (row?.data) {
        try {
          return JSON.parse(row.data as string);
        } catch {
          return null;
        }
      }
    }

    return null;
  }

  /**
   * What is on disk and how complete it is, without deserialising every trip.
   *
   * The prefetcher needs to know which trips are still only summaries and how
   * long ago the full record was written, so it can skip what is already
   * usable offline rather than re-downloading everything on every launch.
   */
  async getCachedTripIndex(): Promise<
    Array<{ id: string; syncedAt: string | null; hasDetail: boolean }>
  > {
    await this.initialize();

    const rows: Array<{ id: string; data: string; synced_at: string | null }> = [];

    if (this.isNative && this.db) {
      const result = await this.db.query('SELECT id, data, synced_at FROM trips');
      for (const row of result.values ?? []) {
        rows.push({
          id: row.id as string,
          data: row.data as string,
          synced_at: (row.synced_at as string) ?? null,
        });
      }
    } else if (this.memDb) {
      for (const row of await this.memDb.getAll('trips')) {
        rows.push({
          id: row.id as string,
          data: row.data as string,
          synced_at: (row.synced_at as string) ?? null,
        });
      }
    }

    return rows.map((row) => {
      let hasDetail = false;
      try {
        const parsed = JSON.parse(row.data) as { days?: unknown };
        hasDetail = Array.isArray(parsed.days);
      } catch {
        // Unreadable row — treat it as missing so the prefetcher replaces it.
      }
      return { id: row.id, syncedAt: row.synced_at, hasDetail };
    });
  }

  /**
   * Get all cached trips.
   */
  async getAllCachedTrips(): Promise<unknown[]> {
    await this.initialize();

    if (this.isNative && this.db) {
      const result = await this.db.query('SELECT data FROM trips ORDER BY updated_at DESC');
      if (result.values) {
        return result.values
          .map((row) => {
            try {
              return JSON.parse(row.data as string);
            } catch {
              return null;
            }
          })
          .filter(Boolean);
      }
      return [];
    } else if (this.memDb) {
      const rows = await this.memDb.getAll('trips');
      return rows
        .map((row) => {
          try {
            return JSON.parse(row.data as string);
          } catch {
            return null;
          }
        })
        .filter(Boolean);
    }

    return [];
  }

  /**
   * Remove a cached trip.
   */
  async removeCachedTrip(tripId: string): Promise<void> {
    await this.initialize();

    if (this.isNative && this.db) {
      await this.db.run('DELETE FROM trips WHERE id = ?', [tripId]);
    } else if (this.memDb) {
      await this.memDb.delete('trips', tripId);
    }
  }

  /**
   * Drops every cached trip the server no longer lists.
   *
   * Without this the cache only ever grew. Leaving a trip — or being removed
   * from one — deletes nothing locally, so the next launch hydrated the home
   * screen from disk and the trip reappeared for the second or so before the
   * network answered. A trip you have been taken off is not a stale copy to
   * refresh; it is one you are not allowed to see, and it has to go.
   *
   * Anything still waiting in the outbox is kept regardless of what the server
   * says: a trip created on a plane does not exist server-side yet, and pruning
   * it would delete the only copy before it was ever sent.
   *
   * @returns how many trips were dropped
   */
  async pruneCachedTripsExcept(serverTripIds: string[]): Promise<number> {
    await this.initialize();

    const keep = new Set(serverTripIds);

    try {
      for (const entry of await this.getPendingMutations()) {
        if (entry.entityType === 'Trip') keep.add(entry.entityId);
      }
    } catch {
      // Unreadable queue — assume something is unsent and prune nothing. The
      // wrong direction here loses data; the right one shows a stale trip for
      // one more launch.
      return 0;
    }

    const cached = await this.getCachedTripIndex();
    const doomed = cached.filter((entry) => !keep.has(entry.id));

    for (const entry of doomed) {
      await this.removeCachedTrip(entry.id);
    }

    return doomed.length;
  }

  // =========================================================================
  // SYNC QUEUE OPERATIONS
  // =========================================================================

  /**
   * Add a mutation to the sync queue for later processing.
   */
  async enqueueMutation(entry: {
    id: string;
    entityType: string;
    entityId: string;
    action: 'CREATE' | 'UPDATE' | 'DELETE';
    payload: unknown;
  }): Promise<void> {
    await this.initialize();

    const now = new Date().toISOString();
    const serializedPayload = JSON.stringify(entry.payload);

    if (this.isNative && this.db) {
      await this.db.run(
        `INSERT INTO sync_queue (id, entity_type, entity_id, action, payload, status, created_at, retry_count)
         VALUES (?, ?, ?, ?, ?, 'PENDING', ?, 0)`,
        [entry.id, entry.entityType, entry.entityId, entry.action, serializedPayload, now],
      );
    } else if (this.memDb) {
      await this.memDb.run('sync_queue', entry.id, {
        id: entry.id,
        entity_type: entry.entityType,
        entity_id: entry.entityId,
        action: entry.action,
        payload: serializedPayload,
        status: 'PENDING',
        created_at: now,
        retry_count: 0,
        last_error: null,
      });
    }
  }

  /**
   * Kills every later mutation for the same entity once one of them is
   * terminal.
   *
   * A CREATE that the server refused means the row does not exist and never
   * will, so the UPDATE and DELETE queued behind it can only ever 404 — and
   * each would burn its own five retries doing so. Nothing about the entity
   * remains in flight, and the user gets one failure to look at instead of
   * three saying the same thing.
   *
   * Only applies to a failed CREATE: if an UPDATE is rejected, later edits to
   * the same row may still be perfectly valid.
   */
  async killDependentMutations(entry: SyncQueueEntry): Promise<number> {
    if (entry.action !== 'CREATE') return 0;

    await this.initialize();

    const reason = `Skipped — creating ${entry.entityType} ${entry.entityId} failed`;

    if (this.isNative && this.db) {
      const result = await this.db.run(
        `UPDATE sync_queue
         SET status = 'DEAD', last_error = ?
         WHERE entity_id = ? AND id != ? AND status IN ('PENDING', 'FAILED')`,
        [reason, entry.entityId, entry.id],
      );
      return result.changes?.changes ?? 0;
    } else if (this.memDb) {
      const rows = await this.memDb.query(
        'sync_queue',
        (row) =>
          row.entity_id === entry.entityId &&
          row.id !== entry.id &&
          (row.status === 'PENDING' || row.status === 'FAILED'),
      );
      for (const row of rows) {
        await this.memDb.run('sync_queue', row.id as string, {
          ...row,
          status: 'DEAD',
          last_error: reason,
        });
      }
      return rows.length;
    }

    return 0;
  }

  /**
   * Mutations the server refused outright. These are not retried — they exist
   * so the UI can tell the user what did not save and let them deal with it.
   */
  async getDeadMutations(): Promise<SyncQueueEntry[]> {
    await this.initialize();

    if (this.isNative && this.db) {
      const result = await this.db.query(
        `SELECT id, entity_type, entity_id, action, payload, status, created_at, retry_count, last_error
         FROM sync_queue
         WHERE status = 'DEAD'
         ORDER BY created_at ASC`,
      );

      return (result.values ?? []).map((row) => ({
        id: row.id as string,
        entityType: row.entity_type as string,
        entityId: row.entity_id as string,
        action: row.action as 'CREATE' | 'UPDATE' | 'DELETE',
        payload: row.payload as string,
        status: row.status as SyncStatus,
        createdAt: row.created_at as string,
        retryCount: row.retry_count as number,
        lastError: (row.last_error as string) ?? null,
        lastErrorCode: (row.last_error_code as string) ?? null,
      }));
    } else if (this.memDb) {
      const rows = await this.memDb.query('sync_queue', (row) => row.status === 'DEAD');
      return rows.map((row) => ({
        id: row.id as string,
        entityType: row.entity_type as string,
        entityId: row.entity_id as string,
        action: row.action as 'CREATE' | 'UPDATE' | 'DELETE',
        payload: row.payload as string,
        status: row.status as SyncStatus,
        createdAt: row.created_at as string,
        retryCount: row.retry_count as number,
        lastError: (row.last_error as string) ?? null,
        lastErrorCode: (row.last_error_code as string) ?? null,
      }));
    }

    return [];
  }

  /**
   * Get all pending mutations from the sync queue, ordered by creation time.
   */
  async getPendingMutations(): Promise<SyncQueueEntry[]> {
    await this.initialize();

    if (this.isNative && this.db) {
      const result = await this.db.query(
        `SELECT id, entity_type, entity_id, action, payload, status, created_at, retry_count, last_error
         FROM sync_queue
         WHERE status IN ('PENDING', 'FAILED')
         ORDER BY created_at ASC`,
      );

      if (result.values) {
        return result.values.map((row) => ({
          id: row.id as string,
          entityType: row.entity_type as string,
          entityId: row.entity_id as string,
          action: row.action as 'CREATE' | 'UPDATE' | 'DELETE',
          payload: row.payload as string,
          status: row.status as SyncStatus,
          createdAt: row.created_at as string,
          retryCount: row.retry_count as number,
          lastError: (row.last_error as string) ?? null,
          lastErrorCode: (row.last_error_code as string) ?? null,
        }));
      }

      return [];
    } else if (this.memDb) {
      const rows = await this.memDb.query(
        'sync_queue',
        (row) => row.status === 'PENDING' || row.status === 'FAILED',
      );

      return rows
        .sort(
          (a, b) =>
            new Date(a.created_at as string).getTime() - new Date(b.created_at as string).getTime(),
        )
        .map((row) => ({
          id: row.id as string,
          entityType: row.entity_type as string,
          entityId: row.entity_id as string,
          action: row.action as 'CREATE' | 'UPDATE' | 'DELETE',
          payload: row.payload as string,
          status: row.status as SyncStatus,
          createdAt: row.created_at as string,
          retryCount: row.retry_count as number,
          lastError: (row.last_error as string) ?? null,
          lastErrorCode: (row.last_error_code as string) ?? null,
        }));
    }

    return [];
  }

  /**
   * Update a sync queue entry's status.
   */
  async updateMutationStatus(
    entryId: string,
    status: SyncStatus,
    error?: string,
    errorCode?: string | null,
  ): Promise<void> {
    await this.initialize();

    if (this.isNative && this.db) {
      if (error) {
        await this.db.run(
          `UPDATE sync_queue
           SET status = ?, last_error = ?, last_error_code = ?, retry_count = retry_count + 1
           WHERE id = ?`,
          [status, error, errorCode ?? null, entryId],
        );
      } else {
        await this.db.run(`UPDATE sync_queue SET status = ? WHERE id = ?`, [status, entryId]);
      }
    } else if (this.memDb) {
      const existing = await this.memDb.get('sync_queue', entryId);
      if (existing) {
        await this.memDb.run('sync_queue', entryId, {
          ...existing,
          status,
          last_error: error ?? existing.last_error,
          last_error_code: error ? (errorCode ?? null) : existing.last_error_code,
          retry_count: error
            ? (existing.retry_count as number) + 1
            : (existing.retry_count as number),
        });
      }
    }
  }

  /**
   * Puts a given-up-on mutation back in the queue.
   *
   * Resets the attempt count, which is not optional: the engine gives up the
   * moment it sees `retryCount >= MAX_RETRIES`, so an entry that died of
   * exhausted retries would be killed again on the next pass without a single
   * request being sent. Clearing the error too, so a stale reason cannot
   * outlive the failure it described.
   */
  async reviveMutation(entryId: string): Promise<void> {
    await this.initialize();

    if (this.isNative && this.db) {
      await this.db.run(
        `UPDATE sync_queue SET status = 'PENDING', retry_count = 0, last_error = NULL WHERE id = ?`,
        [entryId],
      );
    } else if (this.memDb) {
      const existing = await this.memDb.get('sync_queue', entryId);
      if (existing) {
        await this.memDb.run('sync_queue', entryId, {
          ...existing,
          status: 'PENDING',
          retry_count: 0,
          last_error: null,
        });
      }
    }
  }

  /**
   * Sends a refused settlement back, this time saying it is not a duplicate.
   *
   * The payload is rewritten rather than rebuilt from the screen, because by the
   * time anyone sees the dialog the original form is long gone — this is a
   * change made offline, possibly days ago. Reviving alone would only earn the
   * same refusal.
   */
  async confirmDuplicateAndRevive(entryId: string): Promise<void> {
    await this.initialize();

    const withConfirmation = (payload: string): string => {
      try {
        return JSON.stringify({ ...JSON.parse(payload), confirmDuplicate: true });
      } catch {
        // Unparseable payload: leave it as it is and let the replay fail loudly
        // rather than replacing somebody's data with a guess.
        return payload;
      }
    };

    if (this.isNative && this.db) {
      const rows = await this.db.query(`SELECT payload FROM sync_queue WHERE id = ?`, [entryId]);
      const payload = rows.values?.[0]?.payload as string | undefined;
      if (payload === undefined) return;

      await this.db.run(
        `UPDATE sync_queue
         SET payload = ?, status = 'PENDING', retry_count = 0,
             last_error = NULL, last_error_code = NULL
         WHERE id = ?`,
        [withConfirmation(payload), entryId],
      );
    } else if (this.memDb) {
      const existing = await this.memDb.get('sync_queue', entryId);
      if (existing) {
        await this.memDb.run('sync_queue', entryId, {
          ...existing,
          payload: withConfirmation(existing.payload as string),
          status: 'PENDING',
          retry_count: 0,
          last_error: null,
          last_error_code: null,
        });
      }
    }
  }

  /**
   * Drops one entry for good.
   *
   * The only way a DEAD mutation ever leaves the queue. Without it the failure
   * count can never return to zero, so the status indicator stays red for the
   * life of the install and stops meaning anything.
   */
  async discardMutation(entryId: string): Promise<void> {
    await this.initialize();

    if (this.isNative && this.db) {
      await this.db.run('DELETE FROM sync_queue WHERE id = ?', [entryId]);
    } else if (this.memDb) {
      await this.memDb.delete('sync_queue', entryId);
    }
  }

  /**
   * Remove synced mutations from the queue.
   */
  async purgeSyncedMutations(): Promise<number> {
    await this.initialize();

    if (this.isNative && this.db) {
      const result = await this.db.run("DELETE FROM sync_queue WHERE status = 'SYNCED'");
      return result.changes?.changes ?? 0;
    } else if (this.memDb) {
      return this.memDb.deleteWhere('sync_queue', (row) => row.status === 'SYNCED');
    }

    return 0;
  }

  /**
   * Get the count of pending mutations.
   */
  async getPendingMutationCount(): Promise<number> {
    await this.initialize();

    if (this.isNative && this.db) {
      const result = await this.db.query(
        "SELECT COUNT(*) as count FROM sync_queue WHERE status IN ('PENDING', 'FAILED')",
      );
      if (result.values && result.values.length > 0) {
        return result.values[0].count as number;
      }
      return 0;
    } else if (this.memDb) {
      const rows = await this.memDb.query(
        'sync_queue',
        (row) => row.status === 'PENDING' || row.status === 'FAILED',
      );
      return rows.length;
    }

    return 0;
  }

  // =========================================================================
  // SYNC META OPERATIONS
  // =========================================================================

  /**
   * Get a sync metadata value.
   */
  async getSyncMeta(key: string): Promise<string | null> {
    await this.initialize();

    if (this.isNative && this.db) {
      const result = await this.db.query('SELECT value FROM sync_meta WHERE key = ?', [key]);
      if (result.values && result.values.length > 0) {
        return result.values[0].value as string;
      }
      return null;
    } else if (this.memDb) {
      const row = await this.memDb.get('sync_meta', key);
      return (row?.value as string) ?? null;
    }

    return null;
  }

  /**
   * Set a sync metadata value.
   */
  async setSyncMeta(key: string, value: string): Promise<void> {
    await this.initialize();

    const now = new Date().toISOString();

    if (this.isNative && this.db) {
      await this.db.run(
        `INSERT OR REPLACE INTO sync_meta (key, value, updated_at)
         VALUES (?, ?, ?)`,
        [key, value, now],
      );
    } else if (this.memDb) {
      await this.memDb.run('sync_meta', key, {
        key,
        value,
        updated_at: now,
      });
    }
  }

  // =========================================================================
  // UTILITY OPERATIONS
  // =========================================================================

  /**
   * Clear all cached data (but keep sync queue).
   */
  async clearCache(): Promise<void> {
    await this.initialize();

    if (this.isNative && this.db) {
      await this.db.execute('DELETE FROM travel_points;');
      await this.db.execute('DELETE FROM checklist_items;');
      await this.db.execute('DELETE FROM trip_days;');
      await this.db.execute('DELETE FROM trip_expenses;');
      await this.db.execute('DELETE FROM trips;');
    } else if (this.memDb) {
      await this.memDb.clear('travel_points');
      await this.memDb.clear('checklist_items');
      await this.memDb.clear('trip_days');
      await this.memDb.clear('trip_expenses');
      await this.memDb.clear('trips');
    }
  }

  /**
   * Clear everything including sync queue.
   */
  async clearAll(): Promise<void> {
    await this.initialize();

    if (this.isNative && this.db) {
      await this.db.execute('DELETE FROM sync_queue;');
      await this.db.execute('DELETE FROM sync_meta;');
      await this.db.execute('DELETE FROM travel_points;');
      await this.db.execute('DELETE FROM checklist_items;');
      await this.db.execute('DELETE FROM trip_days;');
      await this.db.execute('DELETE FROM trips;');
    } else if (this.memDb) {
      await this.memDb.clearAll();
    }
  }

  /**
   * Check if the database is using native SQLite or in-memory fallback.
   */
  isUsingNativeSQLite(): boolean {
    return this.isNative && this.db !== null;
  }

  /**
   * Close the database connection. Should be called on app shutdown.
   */
  /**
   * Close the connection and end the session. The account's data stays on
   * disk — this is what logging out does, so a queue of unsent changes
   * survives until that person signs back in.
   */
  async close(): Promise<void> {
    if (this.isNative && this.db && this.sqliteConnection && this.currentUserId) {
      await this.db.close();
      await this.sqliteConnection.closeConnection(dbNameForUser(this.currentUserId), false);
      this.db = null;
    }

    // The web fallback lives in memory, so it goes with the session either way.
    this.memDb = null;
    this.currentUserId = null;
    this.initialized = false;
    this.initPromise = null;

    // Anything that waits from here on waits for the *next* session.
    this.sessionGate = new Promise((resolve) => {
      this.openSessionGate = resolve;
    });
  }
}

// ---------------------------------------------------------------------------
// Singleton Export
// ---------------------------------------------------------------------------

/**
 * The singleton offline database instance.
 *
 * Usage:
 *   import { offlineDb } from '@/lib/db/offline-db';
 *
 *   // Cache a trip
 *   await offlineDb.cacheTrip(tripId, tripData);
 *
 *   // Get cached trip (for offline reads)
 *   const trip = await offlineDb.getCachedTrip(tripId);
 *
 *   // Queue a mutation for later sync
 *   await offlineDb.enqueueMutation({
 *     id: uuid(),
 *     entityType: 'travel_point',
 *     entityId: pointId,
 *     action: 'CREATE',
 *     payload: createPayload,
 *   });
 *
 *   // Get pending mutations to sync
 *   const pending = await offlineDb.getPendingMutations();
 */
export const offlineDb = OfflineDatabase.getInstance();

export default offlineDb;
